import React, { useState } from 'react';
import { AlertCircle, Info, Calendar } from 'lucide-react';
import { WizardLayout, ButtonGroup } from './WizardLayout';

interface Step2Props {
  onNext: (data: any) => void;
  onBack: () => void;
  data: any;
}

export default function Step2({ onNext, onBack, data }: Step2Props) {
  const [formData, setFormData] = useState({
    business_type: data.business_type || '',
    event_type: data.event_type || '',
    event_start_date: data.event_start_date || '',
    event_end_date: data.event_end_date || '',
    fema_declaration_number: data.fema_declaration_number || '',
    fema_notes: data.fema_notes || '',
    calculation_mode: data.calculation_mode || 'standard_bi'
  });
  
  const [showEventInfo, setShowEventInfo] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const businessTypes = [
    'Restaurant', 
    'Retail', 
    'Services', 
    'Manufacturing', 
    'Nonprofit/Charity',
    'Healthcare',
    'Hospitality/Tourism',
    'Construction',
    'Other'
  ];
  
  const eventTypes = [
    {
      value: 'hurricane', 
      label: 'Hurricane / Tropical Storm', 
      threshold: '20%',
      description: 'NOAA-verified storm damage, flooding, evacuation losses',

    },
    { 
      value: 'government_shutdown', 
      label: 'Government Shutdown / Furlough', 
      threshold: '15%',
      description: 'Federal contract delays, reduced government spending impact',

    },
    { 
      value: 'tariffs', 
      label: 'Tariffs / Trade War', 
      threshold: '18%',
      description: 'Import cost increases, supply chain disruptions (18.6% avg)',

    },
    { 
      value: 'wildfire', 
      label: 'Wildfire / Fire Damage', 
      threshold: '25%',
      description: 'Fire damage, smoke evacuation, tourism decline ($89B 2025)',

    },
    { 
      value: 'pandemic', 
      label: 'Pandemic / Public Health Emergency', 
      threshold: '8%',
      description: 'Lockdowns, capacity restrictions (COVID-19 standard)',

    },
    { 
      value: 'flood', 
      label: 'Flood / Storm Surge', 
      threshold: '22%',
      description: 'FEMA-verified flooding, evacuation, infrastructure damage',

    },
    { 
      value: 'earthquake', 
      label: 'Earthquake', 
      threshold: '30%',
      description: 'USGS-verified seismic damage, utility disruption',

    },
    { 
      value: 'civil_unrest', 
      label: 'Civil Unrest / Riots', 
      threshold: '20%',
      description: 'Property damage, curfews, reduced foot traffic',

    },
    { 
      value: 'cyber_attack', 
      label: 'Cyber Attack / Ransomware', 
      threshold: '15%',
      description: 'System downtime, data breach, operational disruption',

    },
    { 
      value: 'toxic_contamination', 
      label: 'Toxic Contamination (PFAS/Chemical)', 
      threshold: '0%',
      description: 'Forced closure for toxic remediation - requires Future Closure mode',

    },
    { 
      value: 'custom', 
      label: 'Custom Event (User-Defined)', 
      threshold: '12%',
      description: 'Flexible parameters for any business disruption',

    }
  ];

  const selectedEvent = eventTypes.find(e => e.value === formData.event_type);

  const handleEventTypeChange = (eventType: string) => {
    setFormData({ ...formData, event_type: eventType });
    setValidationError(null);
  };

  const handleSubmit = () => {
    const missingFields = [];
    
    if (!formData.event_type) missingFields.push('Disruption Event Type');
    if (!formData.business_type) missingFields.push('Business Type');
    
    if (missingFields.length > 0) {
      setValidationError(`Please complete the following required fields: ${missingFields.join(', ')}`);
      return;
    }
    
    console.log(`✅ Calculation mode: ${formData.calculation_mode}`);
    
    onNext({
      ...formData,
      calculation_mode: formData.calculation_mode
    });
  };

  return (
    <WizardLayout
      title="Business & Event Details"
      subtitle="Select your disruption event and business type to configure the claim analysis."
      timeEstimate="3 minutes"
      showAutoSave={true}
    >
        <div className="space-y-6">

        {validationError && (
          <p className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>{validationError}</p>
        )}

        {/* 1. EVENT TYPE - First Selection */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-semibold" style={{ color: 'var(--color-text-secondary)' }}>
              Disruption Event Type <span style={{ color: 'var(--color-needs-human-text)' }}>*</span>
            </label>
            <button
              onClick={() => setShowEventInfo(!showEventInfo)}
              className="text-sm font-medium flex items-center gap-1"
              style={{ color: 'var(--color-needs-human-text)' }}
            >
              <Info className="w-4 h-4" />
              {showEventInfo ? 'Hide' : 'Show'} Info
            </button>
          </div>

          <select
            value={formData.event_type}
            onChange={(e) => handleEventTypeChange(e.target.value)}
            className="w-full px-4 py-3 border-2 rounded-lg focus:border-transparent font-medium"
            style={{ borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }}
          >
            <option value="">-- Select Disruption Event --</option>
            {eventTypes.map((event) => (
              <option key={event.value} value={event.value}>
                {event.label} (Threshold: {event.threshold})
              </option>
            ))}
          </select>
          
          {/* Event Description */}
          {selectedEvent && (
            <div className="mt-3 p-4 border rounded-lg" style={{ background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
              <p className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>
                <strong>{selectedEvent.label}</strong>
              </p>
              <p className="text-sm mt-1" style={{ color: 'var(--color-needs-human-text)' }}>{selectedEvent.description}</p>
              <p className="text-xs mt-2" style={{ color: 'var(--color-needs-human-text)' }}>
                <strong>Causation Threshold:</strong> {selectedEvent.threshold} revenue decline required
              </p>
            </div>
          )}

          {/* Info Panel */}
          {showEventInfo && (
            <div className="mt-4 p-4 border-l-4 rounded" style={{ background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-brand)' }}>
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" style={{ color: 'var(--color-brand)' }} />
                <div className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>
                  <p className="font-semibold mb-2">Event-Specific Causation Tests:</p>
                  <ul className="space-y-1 ml-4">
                    <li>• Each event has industry-standard thresholds (NOAA, USGS, FEMA)</li>
                    <li>• Higher thresholds for acute events (hurricanes, fires)</li>
                    <li>• Recovery lag multipliers applied to final compensation</li>
                    <li>• External validation data integrated where available</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 2. BUSINESS TYPE - Second Selection */}
        <div>
          <label className="block text-sm font-semibold mb-3" style={{ color: 'var(--color-text-secondary)' }}>
            Business Type <span style={{ color: 'var(--color-needs-human-text)' }}>*</span>
          </label>
          <div className="grid grid-cols-2 gap-3">
            {businessTypes.map((type) => (
              <button
                key={type}
                onClick={() => { setFormData({ ...formData, business_type: type }); setValidationError(null); }}
                className="py-3 px-4 rounded-lg border-2 font-semibold transition-colors text-left"
                style={
                  formData.business_type === type
                    ? { borderColor: 'var(--color-brand)', background: 'var(--color-needs-human-tint)', color: 'var(--color-needs-human-text)' }
                    : { borderColor: 'var(--color-border)', color: 'var(--color-text-secondary)' }
                }
              >
                {type}
              </button>
            ))}
          </div>
        </div>

        {/* CALCULATION MODE SELECTOR */}
        <div className="border-2 rounded-lg p-6" style={{ borderColor: 'var(--color-needs-human-tint-bd)', background: 'var(--color-needs-human-tint)' }}>
          <div className="flex items-center gap-2 mb-4">
            <div>
              <h3 className="text-lg font-bold" style={{ color: 'var(--color-needs-human-text)' }}>Calculation Mode</h3>
              <p className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>Select the type of BI calculation</p>
            </div>
          </div>
          
          <div className="space-y-3">
            {/* Standard BI Mode */}
            <label className={`flex items-start space-x-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-white/50 transition-colors ${
              formData.calculation_mode === 'standard_bi' ? 'shadow-md' : ''
            }`} style={
              formData.calculation_mode === 'standard_bi'
                ? { borderColor: 'var(--color-brand)', background: 'var(--color-bg-surface)' }
                : { borderColor: 'var(--color-border)' }
            }>
              <input
                type="radio"
                name="calculation_mode"
                value="standard_bi"
                checked={formData.calculation_mode === 'standard_bi'}
                onChange={(e) => setFormData({ ...formData, calculation_mode: e.target.value })}
                className="mt-1"
                style={{ accentColor: 'var(--color-brand)' }}
              />
              <div className="flex-1">
                <p className="text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>Standard BI Calculator (Actual Incurred)</p>
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Past losses from business interruption. Uses historical P&L data with BI engine,
                  V-Test causation, and an RTP (return-to-pre-event) recovery adjustment. Best for
                  hurricanes, fires, floods, etc.
                </p>
                <p className="text-xs mt-2 font-medium" style={{ color: 'var(--color-needs-human-text)' }}>
                  ✓ Most common for disaster claims
                </p>
              </div>
            </label>

            {/* Future Closure Mode */}
            <label className={`flex items-start space-x-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-white/50 transition-colors ${
              formData.calculation_mode === 'future_closure' ? 'shadow-md' : ''
            }`} style={
              formData.calculation_mode === 'future_closure'
                ? { borderColor: 'var(--color-brand)', background: 'var(--color-bg-surface)' }
                : { borderColor: 'var(--color-border)' }
            }>
              <input
                type="radio"
                name="calculation_mode"
                value="future_closure"
                checked={formData.calculation_mode === 'future_closure'}
                onChange={(e) => setFormData({ ...formData, calculation_mode: e.target.value })}
                className="mt-1"
                style={{ accentColor: 'var(--color-brand)' }}
              />
              <div className="flex-1">
                <p className="text-sm font-bold" style={{ color: 'var(--color-text-primary)' }}>Future Forced Closure (3-Year Pro Forma)</p>
                <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  Future losses from forced business closure (e.g., toxic remediation). Calculates 36-month
                  projections with aggressive continuations + shutdown/restart costs. For PFAS, chemical spills, etc.
                </p>
                <p className="text-xs mt-2 font-medium flex items-start gap-1" style={{ color: 'var(--color-needs-human-text)' }}>
                  <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> For cases where business WILL CLOSE for 2-4 years
                </p>
              </div>
            </label>
          </div>

          {/* Auto-suggest future closure for toxic contamination */}
          {formData.event_type === 'toxic_contamination' && formData.calculation_mode === 'standard_bi' && (
            <div className="mt-4 p-4 border rounded-lg" style={{ background: 'var(--color-needs-human-tint)', borderColor: 'var(--color-needs-human-tint-bd)' }}>
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 mt-0.5 flex-shrink-0" style={{ color: 'var(--color-brand)' }} />
                <div className="text-sm" style={{ color: 'var(--color-needs-human-text)' }}>
                  <p className="font-semibold">Suggested: Switch to Future Closure Mode</p>
                  <p className="mt-1">
                    Toxic contamination cases typically require 3-year forced closures for remediation. 
                    Consider using <strong>Future Forced Closure</strong> mode for pro forma projections.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
        
        {/* Event Coverage Period - Optional */}
        <div className="border-2 rounded-lg p-5" style={{ borderColor: 'var(--color-needs-human-tint-bd)', background: 'var(--color-needs-human-tint)' }}>
          <div className="flex items-center gap-2 mb-4">
            <Calendar className="w-5 h-5" style={{ color: 'var(--color-needs-human-text)' }} />
            <div>
              <p className="text-sm font-semibold" style={{ color: 'var(--color-needs-human-text)' }}>Event Coverage Period (Optional)</p>
              <p className="text-xs" style={{ color: 'var(--color-needs-human-text)' }}>
                If you know the dates of the disruption event, enter them here. Otherwise, you can set them in Step 3.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Event Start Date</label>
              <input
                type="date"
                value={formData.event_start_date}
                onChange={(e) => setFormData({ ...formData, event_start_date: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:border-transparent text-sm"
                style={{ borderColor: 'var(--color-border)' }}
              />
            </div>
            <div>
              <label className="block text-xs font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>Event End Date</label>
              <input
                type="date"
                value={formData.event_end_date}
                onChange={(e) => setFormData({ ...formData, event_end_date: e.target.value })}
                className="w-full px-3 py-2 border rounded-lg focus:border-transparent text-sm"
                style={{ borderColor: 'var(--color-border)' }}
              />
            </div>
          </div>

          {formData.event_start_date && formData.event_end_date && (
            <p className="mt-2 text-xs" style={{ color: 'var(--color-verified)' }}>
              Event period set: {formData.event_start_date} to {formData.event_end_date}
            </p>
          )}
        </div>
        </div>

      {/* Navigation Buttons */}
      <ButtonGroup
        onBack={onBack}
        onNext={handleSubmit}
        nextLabel="Continue to Financial Data"
        backLabel="← Back"
      />
    </WizardLayout>
  );
}
